import api from "./apiClient";
import API_ENDPOINTS from "../config/apiEndpoints";

export const fetchOrganizationCredentialPolicy = async (organizationId) => {
  const url = `${API_ENDPOINTS.CREDENTIALS.GET_ORG_POLICY}?organizationId=${encodeURIComponent(organizationId)}`;
  return api.get(url);
};

export const saveOrganizationCredentialPolicy = async (payload) => {
  return api.post(API_ENDPOINTS.CREDENTIALS.ORG_POLICY_ACTIVITY, payload);
};

export const fetchStaffCredentialStatus = async (staffId, organizationId) => {
  const url = `${API_ENDPOINTS.CREDENTIALS.GET_STAFF_STATUS}?staffId=${encodeURIComponent(staffId)}&organizationId=${encodeURIComponent(organizationId)}`;
  return api.get(url);
};

export const acceptTermsAndConditions = async ({ staffId, emailId, organizationId }) => {
  return api.post(API_ENDPOINTS.CREDENTIALS.ACCEPT_TERMS, {
    StaffId: staffId,
    EmailId: emailId,
    OrganizationId: organizationId,
  });
};

export const changePassword = async (payload) => {
  return api.post(API_ENDPOINTS.CREDENTIALS.CHANGE_PASSWORD, payload);
};

export const changeAccessCode = async (payload) => {
  return api.post(API_ENDPOINTS.CREDENTIALS.CHANGE_ACCESS_CODE, payload);
};

export const validateOrganizationCredentialPolicyForm = (form) => {
  if (!form?.isPolicyEnabled) {
    return { valid: true, expiryDays: null, reminderDays: null };
  }

  const raw = String(form.credentialExpiryDays ?? "").trim();
  if (!raw) {
    return {
      valid: false,
      field: "credentialExpiryDays",
      title: "Credential expiry required",
      message:
        "Please enter how many days before password and access code must be renewed.",
    };
  }

  if (!/^\d+$/.test(raw)) {
    return {
      valid: false,
      field: "credentialExpiryDays",
      title: "Invalid credential expiry",
      message: "Credential expiry must be a whole number of days.",
    };
  }

  const expiryDays = Number(raw);
  if (expiryDays < 1) {
    return {
      valid: false,
      field: "credentialExpiryDays",
      title: "Invalid credential expiry",
      message: "Credential expiry must be at least 1 day. Zero and negative values are not allowed.",
    };
  }

  const reminderRaw = String(form.reminderDaysBeforeExpiry ?? "7").trim();
  if (!reminderRaw) {
    return {
      valid: false,
      field: "reminderDaysBeforeExpiry",
      title: "Reminder period required",
      message: "Please enter how many days before expiry users should be warned.",
    };
  }

  if (!/^\d+$/.test(reminderRaw)) {
    return {
      valid: false,
      field: "reminderDaysBeforeExpiry",
      title: "Invalid reminder period",
      message: "Reminder period must be a whole number of days.",
    };
  }

  const reminderDays = Number(reminderRaw);
  if (reminderDays < 1) {
    return {
      valid: false,
      field: "reminderDaysBeforeExpiry",
      title: "Invalid reminder period",
      message: "Reminder period must be at least 1 day.",
    };
  }

  if (reminderDays >= expiryDays) {
    return {
      valid: false,
      field: "reminderDaysBeforeExpiry",
      title: "Invalid reminder period",
      message: "Reminder period must be less than credential expiry days.",
    };
  }

  return { valid: true, expiryDays, reminderDays };
};

export const getCredentialPolicySuccessMessage = () =>
  "Your organisation credential policy has been updated. Reminder and expiry rules apply to all users except System Administrators.";

export const adminChangeUserPassword = async (payload) => {
  return api.post(API_ENDPOINTS.USER.ACTIVATE_DEACTIVATE_USER, {
    ...payload,
    Action: "ChangePassword",
  });
};

export const adminChangeUserAccessCode = async (payload) => {
  return api.post(API_ENDPOINTS.USER.ACTIVATE_DEACTIVATE_USER, {
    ...payload,
    Action: "ChangeAccessCode",
  });
};

export const parseAdminCredentialChangeResponse = (response) => {
  const row = response?.data?.Table?.[0];
  const result = row?.Result ?? row?.result;
  const ok =
    response?.success === 1 &&
    (result === "1" || result === 1 || result === true);
  return {
    ok,
    message:
      row?.Message ||
      row?.message ||
      response?.message ||
      "Could not update credentials.",
  };
};

export const parseCredentialPolicy = (response) => {
  const defaults = {
    ok: true,
    isPolicyEnabled: false,
    credentialExpiryDays: "",
    reminderDaysBeforeExpiry: "7",
    forceChangeOnFirstLogin: true,
  };

  if (!response || response.success !== 1) {
    const message = String(response?.message || "").trim();
    if (
      !message ||
      message.toLowerCase().includes("policy not found")
    ) {
      return defaults;
    }
    return { ok: false, message: message || "Could not load credential policy." };
  }

  const data = response.data || {};
  const credentialExpiryDays =
    data.PasswordExpiryDays ?? data.AccessCodeExpiryDays ?? "";
  return {
    ok: true,
    isPolicyEnabled: Boolean(data.IsPolicyEnabled),
    credentialExpiryDays,
    reminderDaysBeforeExpiry:
      data.ReminderDaysBeforeExpiry != null && data.ReminderDaysBeforeExpiry !== ""
        ? String(data.ReminderDaysBeforeExpiry)
        : "7",
    forceChangeOnFirstLogin: data.ForceChangeOnFirstLogin !== false,
  };
};

export const parseCredentialStatus = (response) => {
  if (!response || response.success !== 1 || !response.data) {
    return null;
  }
  const d = response.data;
  return {
    requiresTermsAcceptance: Boolean(d.RequiresTermsAcceptance),
    requiresCredentialChange: Boolean(d.RequiresCredentialChange),
    mustChangePassword: Boolean(d.MustChangePassword),
    mustChangeAccessCode: Boolean(d.MustChangeAccessCode),
    passwordExpired: Boolean(d.PasswordExpired),
    accessCodeExpired: Boolean(d.AccessCodeExpired),
    accountStatus: d.AccountStatus,
    credentialExpiryWarning: Boolean(d.CredentialExpiryWarning),
    passwordExpiringSoon: Boolean(d.PasswordExpiringSoon),
    accessCodeExpiringSoon: Boolean(d.AccessCodeExpiringSoon),
    daysUntilPasswordExpiry:
      d.DaysUntilPasswordExpiry != null ? Number(d.DaysUntilPasswordExpiry) : null,
    daysUntilAccessCodeExpiry:
      d.DaysUntilAccessCodeExpiry != null ? Number(d.DaysUntilAccessCodeExpiry) : null,
    isExemptFromExpiryPolicy: Boolean(d.IsExemptFromExpiryPolicy),
  };
};
